"use client";
import GameSetup from "@/app/game/setup.js";
import Footer from "@/components/layout/Footer.js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { LobbySettings } from "@rosette/ts/src/business/game/LobbySettings.js";
import { AssetProvider } from "@/components/assets/AssetContext.js";
import { DeviceInfoProvider } from "@rosette/web_components/src/accessibility/DeviceInfoContext.js";
import GameUI from "@/components/game/GameUI.js";
import { useRouter, useSearchParams } from "next/navigation";
import { useMutation } from "@tanstack/react-query";
import { useOnClient } from "@rosette/web_utils/src/useOnClient.js";
import { EnvProvider } from "@/components/env/EnvContext.js";
import { fadeInOut } from "@/utils/fadeInOut.js";
import type { APIAppEnv } from "@rosette/ts/src/business/api/api_schema.js";
import { toCompleteUser } from "@rosette/ts/src/business/api/api_schema.js";
import type { GameSource } from "@rosette/ts/src/business/game/controller/source/GameSource.js";
import { createGameSource } from "@/components/game/GameUIContext.js";
import { useGamePreferences } from "@/app/(api)/api/getGamePreferences/useGamePreferences.js";
import type { GameClientControls } from "@rosette/ts/src/business/GameClientControls.js";
import {
    readPreferredSettings,
    writePreferredSettings,
} from "@rosette/web_utils/src/local/readPreferredSettings.js";
import { startRemoteGame } from "@/app/game/startRemoteGame.js";
import { useGameAPI, GameAPIProvider } from "@/components/api/GameAPIContext.js";
import { useSyncGameSource } from "@/app/game/useSyncGameSource.js";
import { HomeBackground } from "@rosette/web_components/src/layout/HomeBackground.js";
import { LoadingPlaceholder } from "@rosette/web_components/src/layout/LoadingPlaceholder.js";
import type { GamePageParamValues } from "@/app/game/GamePageParamValues.js";
import { readGamePageParamValues, updateParamValues } from "@/app/game/GamePageParamValues.js";
import { KnownLobbySettings } from "@rosette/ts/src/business/game/KnownLobbySettings.js";
import { showNotification } from "@rosette/web_utils/src/game/notifications.js";
import { DotDotDot } from "@rosette/web_components/src/game/timing/DotDotDot.js";
import Header from "@/components/layout/Header.js";
import { LoadingSpinner } from "@rosette/web_components/src/icon/LoadingSpinner.js";
import { Button } from "@rosette/web_components/src/ui/Button.js";
import { Panel } from "@rosette/web_components/src/ui/Panel.js";


interface GamePageContentProps {
    appEnv: APIAppEnv;
    stripeTestMode: boolean;
}

interface GamePageInternalProps {
    appEnv: APIAppEnv;
    stripeTestMode: boolean;
}

function GamePageInternal({ appEnv, stripeTestMode }: GamePageInternalProps) {
    const router = useRouter();
    const searchParams = useSearchParams();
    const paramValues = readGamePageParamValues(searchParams);
    const onClient = useOnClient();
    const gameAPI = useGameAPI();

    const completeUser = toCompleteUser(appEnv.user);
    const { preferences, savedPreferences } = useGamePreferences(completeUser);

    const clientControls: GameClientControls = useMemo(() => {
        const missing = () => {
            throw new Error("This function is missing!");
        };
        return {
            startGame: missing,
            startingGame: false,
            startGameError: null,
            clearError: missing,
            resignGame: missing,
            abortGame: missing,
            notify: showNotification,
        };
    }, []);

    const setParamValues = useCallback((values: GamePageParamValues) => {
        if (values.settings) {
            writePreferredSettings(values.settings);
        }
        updateParamValues(searchParams, values);
        clientControls.clearError();
    }, [clientControls, searchParams]);

    const [gameSource, setGameSource] = useState<GameSource | null>(null);
    const startGameMutation = useMutation({
        mutationFn: async (settings: LobbySettings) => {
            if (!settings.isRemote() && settings instanceof KnownLobbySettings) {
                setGameSource(createGameSource(
                    completeUser, settings, clientControls, preferences, gameAPI,
                ));
                setParamValues({
                    settings,
                    tab: "play",
                });

            } else {
                await startRemoteGame(router, completeUser, settings, gameAPI);
            }
        },
    });
    clientControls.startGame = settings => startGameMutation.mutate(settings);
    clientControls.startingGame = startGameMutation.isPending;
    clientControls.startGameError = startGameMutation.error;
    clientControls.clearError = () => startGameMutation.reset();
    clientControls.resignGame = useCallback(() => {
        if (!gameSource)
            throw new Error("There is no game active");

        gameSource.handleResign();
    }, [gameSource]);
    clientControls.abortGame = useCallback((path) => {
        if (gameSource) {
            gameSource.recordAbortGame();
        }
        // Don't use the router as sometimes it doesn't refresh properly.
        window.onbeforeunload = null;
        window.location.href = (path ? path.toString() : "/");
    }, [gameSource]);
    useSyncGameSource(gameSource, clientControls, preferences, savedPreferences);

    const triedStartGameRef = useRef(false);
    useEffect(() => {
        if (gameSource || triedStartGameRef.current)
            return;

        let settings = paramValues.settings;
        let writeSettings = false;
        if (paramValues.settingsAreUnknown) {
            settings = readPreferredSettings(completeUser);
            writeSettings = true;
        }

        if (paramValues.tab === "play") {
            triedStartGameRef.current = true;
            setTimeout(() => {
                clientControls.startGame(settings);
            });
        } else if (writeSettings) {
            setParamValues({
                settings,
                tab: paramValues.tab,
            });
        }
    }, [
        gameSource, triedStartGameRef, paramValues,
        clientControls, completeUser, setParamValues,
    ]);

    return (
        <AnimatePresence mode="wait">
            {gameSource && onClient && (
                <motion.div
                    key="game"
                    className="fixed inset-0 w-full h-full"
                    {...fadeInOut()}>

                    <GameUI
                        gameSource={gameSource}
                        clientControls={clientControls} />
                </motion.div>
            )}

            {!gameSource && paramValues.tab !== "play" && (
                <motion.div key="setup" className="flex flex-col w-full">
                    {onClient && (
                        <GameSetup
                            clientControls={clientControls}
                            paramValues={paramValues}
                            setParamValues={setParamValues}
                            stripeTestMode={stripeTestMode} />
                    )}

                    {!onClient && (
                        <HomeBackground fullscreen={true}>
                        <LoadingPlaceholder />
                        </HomeBackground>
                    )}

                    <Footer key="footer" />
                </motion.div>
            )}

            {!gameSource && paramValues.tab === "play" && (
                <motion.div key="loading" className="flex flex-col w-full">
                    <HomeBackground>
                        <Header />
                        <article className="w-full max-w-3xl mx-auto">
                            <h1>
                                {clientControls.startGameError
                                    ? "Unable to start your game"
                                    : "Please wait a few seconds"}
                            </h1>
                            <Panel as="section" className="mb-9 min-h-36">
                                {clientControls.startGameError && (
                                    <>
                                        <p>
                                            There was an error starting your game:
                                            {" "}
                                            <span className="text-danger">
                                                {clientControls.startGameError.message}
                                            </span>
                                        </p>
                                        <Button
                                            tone="accent"
                                            href="/">

                                            Back to Home
                                        </Button>
                                    </>
                                )}

                                {clientControls.startingGame && (
                                    <>
                                        <p>
                                            Starting your game
                                            <DotDotDot enabled={true} />
                                        </p>
                                        <LoadingSpinner />
                                    </>
                                )}
                            </Panel>
                        </article>
                    </HomeBackground>

                    <Footer key="footer" />
                </motion.div>
            )}
        </AnimatePresence>
    );
}

export function GamePage({ appEnv, stripeTestMode }: GamePageContentProps) {
    return (
        <EnvProvider appEnv={appEnv}>
            <GameAPIProvider>
                <DeviceInfoProvider>
                    <AssetProvider>
                        <GamePageInternal appEnv={appEnv} stripeTestMode={stripeTestMode} />
                    </AssetProvider>
                </DeviceInfoProvider>
            </GameAPIProvider>
        </EnvProvider>
    );
}
